import {
  DEFAULT_BLOCK_VISUAL_CONFIG,
  DEFAULT_BOARD_VISUAL_CONFIG,
  DEFAULT_GENERATION_CONFIG,
  DEFAULT_TOPBAR_VISUAL_CONFIG,
  loadBlockVisualConfig,
  loadBoardVisualConfig,
  loadGenerationConfig,
  loadTopBarVisualConfig,
  type BlockVisualConfig,
  type BoardVisualConfig,
  type GenerationConfig,
  type TopBarVisualConfig,
} from '@puzzlemania/blockjam';
import type { Logger } from '@puzzlemania/shared';

/**
 * Module 36 (WP-36.2) — resolve Block Jam's shipped config from the build-time
 * `virtual:blockjam-config` manifest, with the in-bundle TS defaults as fallback.
 *
 * This is the half of the single-source seam that lives in the app. The Vite
 * plugin deliberately hands over RAW JSON (it must not own the schema, and it
 * must not fail a build on a config the Studio is mid-way through writing), so
 * validation happens here with the game package's own Zod loaders.
 *
 * **Fallback, never crash.** A missing, malformed or schema-invalid file falls
 * back to the parity default the library already ships with. That is the same
 * value the game used before this WP existed, so the worst case of a bad config
 * is "the tuning didn't apply", not a black screen. It is logged at warn so the
 * silence is diagnosable.
 */

/** What `resolveBlockJamConfig` produces, ready to inject into the manifest. */
export interface ResolvedBlockJamConfig {
  generation: GenerationConfig;
  visuals: BlockVisualConfig;
  boardVisuals: BoardVisualConfig;
  /**
   * Module 54 (WP-54.2). Unlike the three above it never reaches the plugin
   * manifest: its consumer is `blockjam.css`, via the `--bj-topbar-*` custom
   * properties `composition/topBarVars.ts` writes at boot.
   */
  topbarVisuals: TopBarVisualConfig;
  /** Which keys came from the JSON rather than the default — surfaced for tests/logs. */
  sources: {
    generation: 'json' | 'default';
    visuals: 'json' | 'default';
    boardVisuals: 'json' | 'default';
    topbarVisuals: 'json' | 'default';
  };
}

/** The shape `virtual:blockjam-config` emits. */
export interface RawBlockJamConfigManifest {
  generation: unknown | null;
  blockVisuals: unknown | null;
  boardVisuals: unknown | null;
  topbarVisuals: unknown | null;
}

/**
 * Just the bit of `Logger` this needs. Injected, never a module-level singleton —
 * `@puzzlemania/shared`'s Logger is explicitly constructor-injected, and a
 * convenience static here would have quietly reintroduced the global it avoids.
 */
export type WarnLogger = Pick<Logger, 'warn'>;

function parseOr<T>(
  raw: unknown | null,
  load: (raw: unknown) => T,
  fallback: T,
  label: string,
  logger?: WarnLogger,
) {
  if (raw === null || raw === undefined) return { value: fallback, source: 'default' as const };
  try {
    return { value: load(raw), source: 'json' as const };
  } catch (err) {
    logger?.warn(
      `[blockjam] ${label} failed validation — falling back to the in-bundle default. ` +
        `Fix games/blockjam/config/${label} or re-write it from /dev/blockjam.`,
      err,
    );
    return { value: fallback, source: 'default' as const };
  }
}

/**
 * Validate the raw manifest into typed configs. Pure and injectable — the
 * manifest is a parameter rather than a module import so this is testable
 * without a Vite build, and the logger is optional so it stays side-effect-free
 * when a caller does not supply one.
 */
export function resolveBlockJamConfig(
  manifest: RawBlockJamConfigManifest,
  logger?: WarnLogger,
): ResolvedBlockJamConfig {
  const generation = parseOr(
    manifest.generation,
    loadGenerationConfig,
    DEFAULT_GENERATION_CONFIG,
    'generation.json',
    logger,
  );
  const visuals = parseOr(
    manifest.blockVisuals,
    loadBlockVisualConfig,
    DEFAULT_BLOCK_VISUAL_CONFIG,
    'block-visuals.json',
    logger,
  );
  const boardVisuals = parseOr(
    manifest.boardVisuals,
    loadBoardVisualConfig,
    DEFAULT_BOARD_VISUAL_CONFIG,
    'board-visuals.json',
    logger,
  );
  const topbarVisuals = parseOr(
    manifest.topbarVisuals,
    loadTopBarVisualConfig,
    DEFAULT_TOPBAR_VISUAL_CONFIG,
    'topbar-visuals.json',
    logger,
  );
  return {
    generation: generation.value,
    visuals: visuals.value,
    boardVisuals: boardVisuals.value,
    topbarVisuals: topbarVisuals.value,
    sources: {
      generation: generation.source,
      visuals: visuals.source,
      boardVisuals: boardVisuals.source,
      topbarVisuals: topbarVisuals.source,
    },
  };
}
